import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { Network } from '@capacitor/network';

/**
 * Check if the app is running on a mobile device
 */
export const isMobile = () => {
  return Capacitor.isNativePlatform();
};

/**
 * Check if the app is running on iOS
 */
export const isIOS = () => {
  return Capacitor.getPlatform() === 'ios';
};

/**
 * Check if the app is running on Android
 */
export const isAndroid = () => {
  return Capacitor.getPlatform() === 'android';
};

/**
 * Set the status bar style
 */
export const setStatusBarStyle = async (dark: boolean = false) => {
  if (isMobile()) {
    await StatusBar.setStyle({
      style: dark ? Style.Dark : Style.Light,
    });
  }
};

/**
 * Set the status bar background color
 */
export const setStatusBarColor = async (color: string) => {
  if (isMobile()) {
    await StatusBar.setBackgroundColor({ color });
  }
};

/**
 * Trigger haptic feedback
 */
export const triggerHaptic = async (style: ImpactStyle = ImpactStyle.Medium) => {
  if (isMobile()) {
    await Haptics.impact({ style });
  }
};

/**
 * Check network status
 */
export const getNetworkStatus = async () => {
  if (isMobile()) {
    return await Network.getStatus();
  }
  return { connected: navigator.onLine, connectionType: 'unknown' };
};

/**
 * Listen for network status changes
 */
export const onNetworkChange = (callback: (status: any) => void) => {
  if (isMobile()) {
    Network.addListener('networkStatusChange', callback);
  } else {
    // Fallback for web
    window.addEventListener('online', () => callback({ connected: true }));
    window.addEventListener('offline', () => callback({ connected: false }));
  }
};

/**
 * Mobile-friendly viewport configuration
 */
export const setupMobileViewport = () => {
  if (isMobile()) {
    // Prevent zoom on input focus
    const viewport = document.querySelector('meta[name=viewport]');
    if (viewport) {
      viewport.setAttribute('content', 
        'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no'
      );
    }
  }
};

/**
 * Safe area utilities for devices with notches
 */
export const getSafeAreaInsets = () => {
  if (isMobile()) {
    const style = getComputedStyle(document.documentElement);
    return {
      top: style.getPropertyValue('--safe-area-inset-top') || '0px',
      bottom: style.getPropertyValue('--safe-area-inset-bottom') || '0px',
      left: style.getPropertyValue('--safe-area-inset-left') || '0px',
      right: style.getPropertyValue('--safe-area-inset-right') || '0px',
    };
  }
  return { top: '0px', bottom: '0px', left: '0px', right: '0px' };
};